import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Deep-link target for the setup checklist.
 *
 * Checklist items used to send every one of twenty-one steps to the same bare
 * settings URL, so "Connect eBay" and "Map your tax rates" landed on the same
 * page with fourteen collapsed panels and no indication which one was meant.
 *
 * A linked item now carries ?section=<anchor> (a panel) or ?field=<name> (a
 * single form field). This hook tells a component whether it is the thing being
 * asked for, and — when it is — scrolls to it and flashes it so the merchant's
 * eye lands in the right place.
 *
 * Returns { targeted, ref }. Attach ref to the element to reveal.
 */
export function useSetupTarget(kind, name) {
  const location = useLocation();
  const ref = useRef(null);
  const [targeted, setTargeted] = useState(false);

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get(kind);
    const isTarget = Boolean(name) && requested === name;

    setTargeted(isTarget);

    if (!isTarget) return undefined;

    // The panel has to open before it can be scrolled to, and opening is the
    // consumer's job — so wait a frame rather than racing its render.
    const raf = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;

      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    });

    // Long enough to notice, short enough not to linger as decoration.
    const clear = setTimeout(() => setTargeted(false), 2600);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(clear);
    };
  }, [location.search, kind, name]);

  return { targeted, ref };
}

export default useSetupTarget;
